'use client';

import * as React from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Button } from '@/components/ui/button';
import { Field, FieldLabel, Input } from '@/components/ui/field';
import { Alert } from '@/components/ui/primitives';
import { forgotPasswordSchema } from '@/lib/validation/schemas';
import { forgotPasswordAction } from '@/server/actions';

type FormValues = { identifier: string };

export function ForgotPasswordForm() {
  const [sent, setSent] = React.useState<{ message: string; devToken?: string } | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({
    resolver: zodResolver(forgotPasswordSchema),
    defaultValues: { identifier: '' },
  });

  async function onSubmit(values: FormValues) {
    const result = await forgotPasswordAction(values);
    if (result.ok) {
      setSent({ message: result.message ?? 'Request received.', devToken: result.data.devToken });
    }
  }

  if (sent) {
    return (
      <div className="mt-4 space-y-3">
        <Alert tone="positive" title="Request received">
          {sent.message}
        </Alert>
        {sent.devToken ? (
          <Alert tone="info" title="Development only">
            Email delivery is not configured in this environment, so the reset link is shown here:{' '}
            <a className="font-medium underline" href={`/reset-password?token=${sent.devToken}`}>
              open reset link
            </a>
          </Alert>
        ) : null}
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="mt-5 space-y-4" noValidate>
      <Field error={errors.identifier?.message}>
        <FieldLabel required>Email, username, or mobile</FieldLabel>
        <Input
          {...register('identifier')}
          autoComplete="username"
          autoCapitalize="none"
          spellCheck={false}
          enterKeyHint="go"
        />
      </Field>
      <Button type="submit" block size="lg" loading={isSubmitting} loadingLabel="Sending…">
        Send reset link
      </Button>
    </form>
  );
}
